/**
 * Notification Utilities
 * Helper functions for notifications system
 */

import type { Notification } from '@/types'

/**
 * Map notification types to Material Design Icons
 */
export const getNotificationIcon = (type: string): string => {
  const iconMap: Record<string, string> = {
    follower: 'mdi-account-plus',
    like: 'mdi-heart',
    comment: 'mdi-comment',
    mention: 'mdi-at',
    group_invite: 'mdi-account-group',
    event_reminder: 'mdi-calendar-alert',
    badge_earned: 'mdi-trophy',
    message: 'mdi-message',
    reply: 'mdi-reply',
    vote: 'mdi-thumb-up',
    group_post: 'mdi-forum',
    event_update: 'mdi-calendar-edit',
    challenge_completed: 'mdi-star',
    level_up: 'mdi-rocket-launch',
    system: 'mdi-information',
  }

  return iconMap[type] || 'mdi-bell'
}

/**
 * Map notification types to colors
 */
export const getNotificationColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    follower: 'primary',
    like: 'error',
    comment: 'info',
    mention: 'warning',
    group_invite: 'success',
    event_reminder: 'warning',
    badge_earned: 'amber',
    message: 'primary',
    reply: 'info',
    vote: 'success',
    group_post: 'purple',
    event_update: 'orange',
    challenge_completed: 'success',
    level_up: 'purple',
    system: 'grey',
  }

  return colorMap[type] || 'grey'
}

/**
 * Format relative time for notifications
 * Examples: "just now", "2 minutes ago", "1 hour ago", "2 days ago"
 */
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 10) {
    return 'just now'
  }

  if (seconds < 60) {
    return `${seconds} seconds ago`
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  }

  const days = Math.floor(hours / 24)
  if (days < 7) {
    return days === 1 ? '1 day ago' : `${days} days ago`
  }

  const weeks = Math.floor(days / 7)
  if (weeks < 4) {
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
  }

  const months = Math.floor(days / 30)
  if (months < 12) {
    return months === 1 ? '1 month ago' : `${months} months ago`
  }

  const years = Math.floor(days / 365)
  return years === 1 ? '1 year ago' : `${years} years ago`
}

/**
 * Get navigation route from notification
 * Returns the route to navigate to when clicking a notification
 */
export const getNotificationRoute = (notification: Notification): string => {
  const { type, link, metadata } = notification

  // If explicit link is provided, use it
  if (link) {
    return link
  }

  // Otherwise, construct route based on type and metadata
  switch (type) {
    case 'follower':
      return metadata?.user_id ? `/profile/${metadata.user_id}` : '/profile'

    case 'like':
    case 'comment':
    case 'reply':
      if (metadata?.content_id) {
        return `/content/${metadata.content_id}`
      }
      if (metadata?.discussion_id) {
        return `/discussions/${metadata.discussion_id}`
      }
      return '/notifications'

    case 'mention':
      if (metadata?.content_id) {
        return `/content/${metadata.content_id}`
      }
      if (metadata?.discussion_id) {
        return `/discussions/${metadata.discussion_id}`
      }
      if (metadata?.comment_id) {
        return `/content/${metadata.target_id}#comment-${metadata.comment_id}`
      }
      return '/notifications'

    case 'group_invite':
    case 'group_post':
      return metadata?.group_id ? `/groups/${metadata.group_id}` : '/groups'

    case 'event_reminder':
    case 'event_update':
      return metadata?.event_id ? `/events/${metadata.event_id}` : '/events'

    case 'badge_earned':
      return '/profile#badges'

    case 'message':
      return metadata?.conversation_id ? `/messages/${metadata.conversation_id}` : '/messages'

    case 'vote':
      if (metadata?.discussion_id) {
        return `/discussions/${metadata.discussion_id}`
      }
      return '/notifications'

    case 'challenge_completed':
    case 'level_up':
      return '/leaderboard'

    default:
      return '/notifications'
  }
}

/**
 * Truncate notification message to specified length
 */
export const truncateMessage = (message: string, maxLength: number = 100): string => {
  if (message.length <= maxLength) {
    return message
  }
  return message.substring(0, maxLength).trim() + '...'
}
